import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { api } from '../services/api';
import { ContentPiece, TopicProposal } from '../types';

export function useWeekContent(weekId: string | undefined) {
  const [content, setContent] = useState<Record<string, ContentPiece>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!weekId) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'weeks', weekId, 'content'),
      (snap) => {
        const pieces: Record<string, ContentPiece> = {};
        snap.docs.forEach((d) => {
          pieces[d.id] = { ...(d.data() as ContentPiece) };
        });
        setContent(pieces);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [weekId]);

  return { content, loading };
}

export function useGenerateTopics(weekId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (context?: string) =>
      api.post('/generate/topics', { weekId, context }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', weekId] }),
  });
}

export function useGenerateOutline(weekId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (topic: TopicProposal) =>
      api.post('/generate/outline', { weekId, topic }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', weekId] }),
  });
}

export function useGenerateBlog(weekId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ topic, outline }: { topic: TopicProposal; outline: string }) =>
      api.post('/generate/blog', { weekId, topic, outline }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', weekId] }),
  });
}

export function useGenerateSocial(weekId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ topic, blogContent }: { topic: TopicProposal; blogContent: string }) =>
      api.post('/generate/social', { weekId, topic, blogContent }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', weekId] }),
  });
}

export function useGenerateGraphics(weekId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ topic, blogTitle }: { topic: TopicProposal; blogTitle?: string }) =>
      api.post('/generate/graphics', { weekId, topic, blogTitle }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', weekId] }),
  });
}

export function useContentQuery(weekId: string) {
  return useQuery({
    queryKey: ['content', weekId],
    queryFn: () => Promise.resolve({}),
    enabled: false,
  });
}
